import Link from 'next/link';
import { useEffect, useState } from 'react';

import type { SessionListItem } from '@/services/competencyAssessment';
import { getSessions } from '@/services/competencyAssessment';

const STATUS_STYLES: Record<
  string,
  { bg: string; text: string; label: string }
> = {
  COMPLETED: {
    bg: 'bg-green-100',
    text: 'text-green-800',
    label: 'Zakończona',
  },
  EVALUATING: {
    bg: 'bg-yellow-100',
    text: 'text-yellow-800',
    label: 'Ocenianie…',
  },
  IN_PROGRESS: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'W trakcie' },
  FAILED: { bg: 'bg-red-100', text: 'text-red-800', label: 'Błąd' },
};

export default function AssessmentSessionsPage() {
  const [sessions, setSessions] = useState<SessionListItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getSessions()
      .then(setSessions)
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto max-w-2xl px-4 py-8">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-xl font-semibold text-gray-900">
            Sesje oceny kompetencji
          </h1>
          <Link
            href="/competency-assessment"
            className="rounded bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700"
          >
            Nowa sesja
          </Link>
        </div>

        {loading && (
          <div className="py-16 text-center text-gray-400">Ładowanie…</div>
        )}

        {!loading && sessions.length === 0 && (
          <div className="py-16 text-center text-gray-500">
            Brak sesji.{' '}
            <Link
              href="/competency-assessment"
              className="text-blue-600 underline"
            >
              Rozpocznij pierwszą sesję
            </Link>
          </div>
        )}

        {!loading && sessions.length > 0 && (
          <div className="space-y-2">
            {sessions.map((s) => {
              const style = STATUS_STYLES[s.status] ?? {
                bg: 'bg-gray-100',
                text: 'text-gray-600',
              };
              const date = new Date(s.createdAt).toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
              });

              return (
                <Link
                  key={s.id}
                  href={
                    s.status === 'COMPLETED' || s.status === 'FAILED'
                      ? {
                          pathname: '/competency-assessment/result/[id]',
                          query: { id: String(s.id) },
                        }
                      : {
                          pathname: '/competency-assessment/[id]',
                          query: { id: String(s.id) },
                        }
                  }
                  className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-3 transition-all hover:border-blue-300 hover:shadow-sm"
                >
                  <div>
                    <div className="text-sm font-medium capitalize text-gray-900">
                      {s.competency.replace(/_/g, ' ')}
                    </div>
                    <div className="mt-0.5 text-xs text-gray-400">
                      #{s.id} · {date}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="rounded-full bg-gray-100 px-2 py-1 text-xs font-medium uppercase text-gray-500">
                      {s.language ?? 'pl'}
                    </span>
                    <span
                      className={`rounded-full px-2.5 py-1 text-xs font-medium ${style.bg} ${style.text}`}
                    >
                      {style.label}
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
